import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { logProductionEvent, PRODUCTION_EVENT_TYPES } from '../../utils/productionEvents';
import { useAuth } from '../../context/AuthContext';
import { useTaskCategories, useProductionAreas, useFloorWorkers, useTaskTags } from '../../hooks/useCompanyData';
import TaskTagPicker from '../../components/common/TaskTagPicker';
import { resolveTaskTagFields, recordRecentTagIds, getRecentTagIds } from '../../utils/taskTags';
import { useToast } from '../../components/common/Toast';
import { toDatetimeLocal, fromDatetimeLocal } from '../../utils/time';
import { ChevronLeft, PenLine, Check } from 'lucide-react';
import './StartTaskPage.css';

// 2 steps: task selection → launch (assignment + time + room)
const STEPS = ['task', 'launch'];

const TASK_TYPES = [
  { type: 'production', descKey: 'production.taskTypeProduction' },
  { type: 'support',    descKey: 'production.taskTypeSupport' },
  { type: 'delay',      descKey: 'production.taskTypeDelay' },
  { type: 'other',      descKey: 'production.taskTypeOther' },
];

export default function StartTaskPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, companyId } = useAuth();
  const toast = useToast();
  const { categories } = useTaskCategories();
  const { tags } = useTaskTags();
  const { areas } = useProductionAreas();
  const { workers } = useFloorWorkers();

  // Prefill from "Start Again" on Recent Tasks page
  const prefill = location.state?.prefill ?? null;

  const [step, setStep] = useState(prefill ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showManualTime, setShowManualTime] = useState(false);

  const [form, setForm] = useState({
    taskMode: prefill?.taskMode ?? 'preset',
    taskCategoryId: prefill?.taskCategoryId ?? '',
    taskCategoryName: prefill?.taskCategoryName ?? '',
    taskType: prefill?.taskType ?? '',
    taskName: prefill?.taskName ?? '',
    isCustomTaskName: prefill?.isCustomTaskName ?? false,
    customTaskName: prefill?.customTaskName ?? '',
    customTaskType: prefill?.customTaskType ?? '',
    assignmentType: 'individual',
    employeeIds: [],
    employeeNames: [],
    teamSize: 1,
    startTime: toDatetimeLocal(new Date()),
    roomId: '',
    roomName: '',
    tagIds: [],
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Auto-select the logged-in worker when the list loads (individual mode, once).
  // Floor worker doc IDs are not auth UIDs — match by display name, or pick the
  // sole active worker when there is only one on the roster.
  const selfSelectedRef = useRef(false);
  useEffect(() => {
    if (selfSelectedRef.current || workers.length === 0) return;
    const active = workers.filter((w) => w.active !== false);
    if (active.length === 0) return;

    const displayName = profile?.displayName?.trim().toLowerCase();
    const self = displayName
      ? active.find((w) => w.name?.trim().toLowerCase() === displayName)
      : active.find((w) => w.id === user?.uid);
    const pick = self ?? (active.length === 1 ? active[0] : null);
    if (!pick) return;

    selfSelectedRef.current = true;
    setForm((f) => ({
      ...f,
      employeeIds: [pick.id],
      employeeNames: [pick.name ?? profile?.displayName ?? ''],
      teamSize: 1,
    }));
  }, [workers, user?.uid, profile?.displayName]);

  const activeWorkers = workers.filter((w) => w.active !== false);

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (form.taskMode === 'preset') {
        if (!form.taskCategoryId) e.taskCategoryId = t('validation.required');
      } else {
        if (!form.customTaskName.trim()) e.customTaskName = t('validation.required');
        if (!form.customTaskType) e.customTaskType = t('validation.taskTypeRequired');
      }
    }
    if (step === 1) {
      if (form.assignmentType === 'individual' && activeWorkers.length > 0 && form.employeeIds.length === 0)
        e.employeeIds = t('validation.minOneEmployee');
      if (form.assignmentType === 'team' && (!form.teamSize || form.teamSize < 1))
        e.teamSize = t('validation.teamSizeMin');
      if (showManualTime && !form.startTime) {
        e.startTime = t('validation.required');
      } else if (showManualTime && form.startTime) {
        const start = fromDatetimeLocal(form.startTime);
        if (start && start > new Date()) e.startTime = t('validation.startInFuture');
        else if (start && (Date.now() - start.getTime()) > 24 * 3600 * 1000)
          e.startTimeWarning = t('production.startTimePastWarning');
      }
    }
    setErrors(e);
    const WARNINGS = ['startTimeWarning'];
    return Object.keys(e).filter((k) => !WARNINGS.includes(k)).length === 0;
  };

  // Preset category tap → select + auto-advance to launch step
  const handleCategorySelect = (cat) => {
    setForm((f) => ({
      ...f,
      taskMode: 'preset',
      taskCategoryId: cat.id,
      taskCategoryName: cat.name,
      taskType: cat.type,
      taskName: cat.name,
      isCustomTaskName: false,
      customTaskName: '',
      customTaskType: '',
    }));
    setErrors({});
    setStep(1);
  };

  const handleSwitchToCustom = () => {
    setForm((f) => ({
      ...f,
      taskMode: 'custom',
      taskCategoryId: '',
      taskCategoryName: '',
      taskType: '',
      taskName: '',
      isCustomTaskName: true,
    }));
    setErrors({});
  };

  const handleSwitchToPreset = () => {
    setForm((f) => ({
      ...f,
      taskMode: 'preset',
      customTaskName: '',
      customTaskType: '',
      isCustomTaskName: false,
    }));
    setErrors({});
  };

  // "Next" is only used in custom task mode (step 0 → step 1).
  // Preset mode auto-advances on category tap.
  const handleNext = () => {
    if (!validate()) return;
    if (step === 0) setStep(1);
    else handleStart();
  };

  const handleStart = async () => {
    if (!validate()) return;
    if (loading) return; // guard against double-submit
    setLoading(true);
    try {
      const startTs = showManualTime
        ? Timestamp.fromDate(fromDatetimeLocal(form.startTime) || new Date())
        : Timestamp.fromDate(new Date());
      const isCustom = form.taskMode === 'custom';
      const teamSize = form.assignmentType === 'individual'
        ? Math.max(1, form.employeeIds.length)
        : Number(form.teamSize) || 1;

      const tagsById = new Map(tags.map((tg) => [tg.id, tg]));
      const tagFields = resolveTaskTagFields(form.tagIds, tagsById);

      const taskPayload = {
        companyId,
        taskName: isCustom ? form.customTaskName.trim() : form.taskName,
        taskCategoryId: isCustom ? null : form.taskCategoryId,
        taskCategoryName: isCustom ? null : form.taskCategoryName,
        taskType: isCustom ? form.customTaskType : form.taskType,
        isCustomTaskName: isCustom,
        customTaskName: isCustom ? form.customTaskName.trim() : null,
        status: 'active',
        assignmentType: form.assignmentType,
        employeeIds: form.assignmentType === 'individual' ? form.employeeIds : [],
        employeeNames: form.assignmentType === 'individual' ? form.employeeNames : [],
        teamSize,
        startTime: startTs,
        endTime: null,
        durationMinutes: null,
        labourMinutes: null,
        labourHours: null,
        labourCost: null,
        labourCostCurrency: null,
        labourRateSource: 'unavailable',
        labourCostCalculatedAt: null,
        productName: null,
        sku: null,
        lotNumber: null,
        quantityCompleted: null,
        unitType: null,
        unitsPerHour: null,
        unitsPerLabourHour: null,
        costPerUnit: null,
        roomId: form.roomId || null,
        roomName: form.roomName || null,
        downtimeMinutes: null,
        downtimeReason: null,
        notes: null,
        createdBy: user.uid,
        submittedBy: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        submittedAt: null,
        editedBy: null,
        editedAt: null,
        editReason: null,
        archived: false,
        archivedAt: null,
        archivedBy: null,
        tagIds: tagFields.tagIds,
        tagNames: tagFields.tagNames,
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTION_TASKS), taskPayload);

      recordRecentTagIds(companyId, tagFields.tagIds);

      logProductionEvent({
        companyId,
        taskId: docRef.id,
        eventType: PRODUCTION_EVENT_TYPES.TASK_STARTED,
        userId: user.uid,
        task: { id: docRef.id, ...taskPayload, startTime: startTs },
        timestamp: startTs,
      });

      toast(t('production.taskRunning'), 'success');
      navigate('/production/active-task');
    } catch (err) {
      toast(t('toast.saveFailed'), 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (uid, name) => {
    const ids = [...form.employeeIds];
    const names = [...form.employeeNames];
    const idx = ids.indexOf(uid);
    if (idx >= 0) {
      if (ids.length === 1) return;
      ids.splice(idx, 1);
      names.splice(idx, 1);
    } else {
      ids.push(uid);
      names.push(name);
    }
    set('employeeIds', ids);
    set('employeeNames', names);
    set('teamSize', ids.length);
  };

  const selectedLabel = form.taskMode === 'custom' ? form.customTaskName : form.taskName;
  const selectedType  = form.taskMode === 'custom' ? form.customTaskType  : form.taskType;

  // Custom mode step 0: need name + type before advancing
  const customStep0Disabled =
    form.taskMode === 'custom' &&
    (!form.customTaskName.trim() || !form.customTaskType);

  return (
    <div className="start-task-page">
      <div className="start-task-header">
        <button
          className="btn btn-icon"
          onClick={() => step === 0 ? navigate('/production') : setStep(0)}
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="font-semibold">{t('production.startNewTask')}</h2>
        <div style={{ width: 38 }} />
      </div>

      {/* Step indicator */}
      <div className="step-indicator">
        {STEPS.map((s, i) => (
          <div key={s} className={`step-dot ${i <= step ? 'active' : ''}`} />
        ))}
      </div>

      {/* ── Step 0: Task selection ── */}
      {step === 0 && (
        <div className="start-task-section">
          {form.taskMode === 'preset' ? (
            <>
              <h3 className="start-task-section-title">{t('production.selectCategory')}</h3>
              <div className="category-grid">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className="category-btn"
                    onClick={() => handleCategorySelect(cat)}
                  >
                    <span className={`badge badge-${cat.type}`} style={{ marginBottom: 4, alignSelf: 'flex-start' }}>
                      {t(`categories.${cat.type}`)}
                    </span>
                    <span className="category-btn-name">{cat.name}</span>
                  </button>
                ))}
                <button className="category-btn category-btn-custom" onClick={handleSwitchToCustom}>
                  <PenLine size={18} />
                  <span className="category-btn-name">{t('production.enterCustomTask')}</span>
                </button>
              </div>
              {/* No bottom button in preset mode — tapping a card IS the action */}
            </>
          ) : (
            /* Custom task entry */
            <div className="custom-task-panel">
              <button className="btn btn-ghost btn-sm" onClick={handleSwitchToPreset} style={{ alignSelf: 'flex-start' }}>
                ← {t('production.backToPresets')}
              </button>

              <div className="form-group">
                <label className="form-label">{t('production.customTaskName')} <span className="required">*</span></label>
                <input
                  className={`form-control form-control-lg ${errors.customTaskName ? 'is-error' : ''}`}
                  placeholder={t('production.customTaskPlaceholder')}
                  value={form.customTaskName}
                  onChange={(e) => set('customTaskName', e.target.value)}
                  autoFocus
                />
                {errors.customTaskName && <p className="form-error">{errors.customTaskName}</p>}
              </div>

              <div className="form-group">
                <label className="form-label">{t('production.selectTaskType')} <span className="required">*</span></label>
                <div className="task-type-grid">
                  {TASK_TYPES.map(({ type, descKey }) => (
                    <button
                      key={type}
                      className={`task-type-btn ${form.customTaskType === type ? 'selected' : ''}`}
                      onClick={() => set('customTaskType', type)}
                    >
                      <span className={`badge badge-${type}`}>{t(`categories.${type}`)}</span>
                      <span className="task-type-desc">{t(descKey)}</span>
                    </button>
                  ))}
                </div>
                {errors.customTaskType && <p className="form-error">{errors.customTaskType}</p>}
              </div>

              <p className="form-hint">{t('production.customTaskHint')}</p>

              <div className="start-task-actions">
                <button
                  className="btn btn-primary btn-lg btn-block"
                  onClick={handleNext}
                  disabled={customStep0Disabled}
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step 1: Launch (assignment + time + room) ── */}
      {step === 1 && (
        <div className="start-task-section">
          {/* Selected task summary — tap to change */}
          <button className="task-summary-bar task-summary-bar-btn" onClick={() => setStep(0)}>
            {selectedType && <span className={`badge badge-${selectedType}`}>{t(`categories.${selectedType}`)}</span>}
            <span className="font-semibold text-sm flex-1 text-left">{selectedLabel}</span>
            {form.taskMode === 'custom' && (
              <span className="badge badge-custom" style={{ fontSize: 10 }}>{t('common.custom')}</span>
            )}
            <span className="task-summary-change">{t('common.change')}</span>
          </button>

          {/* Who is working */}
          <div className="launch-section">
            <p className="launch-label">{t('production.assignmentType')}</p>
            <div className="assignment-toggle">
              <button
                className={`assignment-option ${form.assignmentType === 'individual' ? 'selected' : ''}`}
                onClick={() => set('assignmentType', 'individual')}
              >
                {t('production.individual')}
              </button>
              <button
                className={`assignment-option ${form.assignmentType === 'team' ? 'selected' : ''}`}
                onClick={() => set('assignmentType', 'team')}
              >
                {t('production.team')}
              </button>
            </div>

            {form.assignmentType === 'individual' && (
              <div className="employee-list mt-3">
                {activeWorkers.map((w) => (
                  <button
                    key={w.id}
                    className={`employee-item ${form.employeeIds.includes(w.id) ? 'selected' : ''}`}
                    onClick={() => toggleEmployee(w.id, w.name)}
                  >
                    <div className="employee-avatar">{w.name?.[0] ?? '?'}</div>
                    <span className="employee-item-name">{w.name}</span>
                    {form.employeeIds.includes(w.id) && (
                      <Check size={16} className="employee-check" />
                    )}
                  </button>
                ))}
                {activeWorkers.length === 0 && (
                  <p className="form-hint">{t('production.noWorkersHint')}</p>
                )}
                {errors.employeeIds && <p className="form-error">{errors.employeeIds}</p>}
              </div>
            )}

            {form.assignmentType === 'team' && (
              <div className="form-group mt-3">
                <label className="form-label">{t('production.teamSize')} <span className="required">*</span></label>
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  className={`form-control form-control-lg ${errors.teamSize ? 'is-error' : ''}`}
                  placeholder={t('production.teamSizePlaceholder')}
                  value={form.teamSize || ''}
                  onChange={(e) => set('teamSize', parseInt(e.target.value, 10) || '')}
                />
                {errors.teamSize && <p className="form-error">{errors.teamSize}</p>}
              </div>
            )}
          </div>

          {/* Room (optional) */}
          {areas.length > 0 && (
            <div className="launch-section">
              <p className="launch-label">{t('production.room')} <span className="launch-optional">{t('common.optional')}</span></p>
              <select
                className="form-control form-control-lg"
                value={form.roomId}
                onChange={(e) => {
                  const area = areas.find((a) => a.id === e.target.value);
                  set('roomId', e.target.value);
                  set('roomName', area?.name ?? '');
                }}
              >
                <option value="">— {t('common.optional')} —</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          {tags.length > 0 && (
            <div className="launch-section">
              <p className="launch-label">
                {t('tags.taskTags')} <span className="launch-optional">{t('common.optional')}</span>
              </p>
              <TaskTagPicker
                tags={tags}
                selectedIds={form.tagIds}
                onChange={(ids) => set('tagIds', ids)}
                recentIds={getRecentTagIds(companyId)}
                compact
              />
            </div>
          )}

          {/* Start time — default is now; reveal manual picker on demand */}
          <div className="launch-section">
            {!showManualTime ? (
              <div className="time-now-row">
                <span className="text-sm text-muted">{t('production.timerMode')}</span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { setShowManualTime(true); set('startTime', toDatetimeLocal(new Date())); }}
                >
                  {t('production.startedEarlier')}
                </button>
              </div>
            ) : (
              <>
                <div className="time-now-row">
                  <p className="launch-label">{t('production.startTime')}</p>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowManualTime(false)}>
                    {t('production.useTimerNow')}
                  </button>
                </div>
                <input
                  type="datetime-local"
                  className={`form-control form-control-lg ${errors.startTime ? 'is-error' : ''}`}
                  value={form.startTime}
                  max={toDatetimeLocal(new Date())}
                  onChange={(e) => set('startTime', e.target.value)}
                />
                {errors.startTime && <p className="form-error">{errors.startTime}</p>}
                {!errors.startTime && errors.startTimeWarning && (
                  <p className="form-hint form-hint-warn">{errors.startTimeWarning}</p>
                )}
              </>
            )}
          </div>

          <div className="start-task-actions">
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={handleStart}
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : t('production.startNewTask')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
